import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Send, Sparkles, Upload, Link as LinkIcon, ClipboardPaste,
  FileText, CheckCircle2, XCircle, RotateCw, AlertTriangle, ChevronRight,
  Clock, DollarSign, Cpu, ArrowRight, FolderOpen, Trash2, Loader2,
  FilePlus2, Wand2, Eye, Edit3, BookMarked, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ReadinessDashboard from "./ReadinessDashboard";
import DiscoveryPanel from "./DiscoveryPanel";
import RecommendationsPanel from "./RecommendationsPanel";
import MarkdownContent from "./MarkdownContent";

/**
 * StaffWorkspace — the canonical Founder-facing office for any Digital Staff.
 *
 * First implementation: Senior Grant Writer.
 * Future staff (Volunteer Coordinator, Partnership Director, Media Director, etc.)
 * reuse this shell — only the `capabilities` config changes.
 *
 * Design principles:
 *   1. Conversation is central, but not the only surface. Every artifact
 *      (analysis, draft, memory) lives in a dedicated panel.
 *   2. Nothing goes outside the institution without human approval.
 *   3. Cost is always visible before an AI action.
 *   4. Learning is captured but not injected — the Founder Preferences panel
 *      surfaces patterns for the Founder to see.
 */

const DRAFT_TYPES = [
  { key: "grant_loi", label: "Letter of Inquiry", short: "LOI" },
  { key: "grant_concept_paper", label: "Concept Paper", short: "Concept" },
  { key: "grant_full_proposal", label: "Full Proposal", short: "Full Proposal" },
  { key: "grant_budget_narrative", label: "Budget Narrative", short: "Budget" },
  { key: "grant_executive_summary", label: "Executive Summary", short: "Exec Summary" },
  { key: "grant_support_letter", label: "Support Letter", short: "Support Letter" },
  { key: "grant_app_answers", label: "Application Answers", short: "App Answers" },
];

export default function StaffWorkspace({ staffId, staffName = "Senior Grant Writer", compact = false }) {
  // ---------- data ----------
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [currentThread, setCurrentThread] = useState("global");
  const [chatDraft, setChatDraft] = useState("");
  const [sending, setSending] = useState(false);

  const [intakes, setIntakes] = useState([]);
  const [grants, setGrants] = useState([]);
  const [activeGrantId, setActiveGrantId] = useState(null);
  const [activeAnalysis, setActiveAnalysis] = useState(null); // output row
  const [drafts, setDrafts] = useState([]); // draft outputs for active grant
  const [assignments, setAssignments] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [memory, setMemory] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null); // running job state {milestones, progress}

  const [showIntake, setShowIntake] = useState(false);
  const [confirmCost, setConfirmCost] = useState(null); // {task, onConfirm, estimate}
  const [expandedPanel, setExpandedPanel] = useState("analysis"); // 'analysis' | 'drafts' | 'preferences'
  const messagesEndRef = useRef(null);

  // ---------- loaders ----------
  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/${staffId}/conversation/threads`);
      setThreads(data);
    } catch (e) { console.debug("threads: empty or unreachable", e?.message); }
  }, [staffId]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/${staffId}/conversation`, {
        params: { thread_key: currentThread, limit: 200 },
      });
      setMessages(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [staffId, currentThread]);

  const loadIntakes = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/${staffId}/intakes`);
      setIntakes(data);
    } catch (e) { console.debug("intakes: load failed", e?.message); }
  }, [staffId]);

  const loadGrants = useCallback(async () => {
    try {
      const { data } = await api.get(`/grants`);
      setGrants(data);
    } catch (e) { console.debug("grants: load failed", e?.message); }
  }, []);

  const loadAssignmentsAndDrafts = useCallback(async () => {
    try {
      const [a, o] = await Promise.all([
        api.get(`/staff/${staffId}/assignments`),
        api.get(`/staff/${staffId}/outputs`),
      ]);
      setAssignments(a.data);
      if (activeGrantId) {
        const forGrant = o.data.filter(x => x.target_id === activeGrantId);
        const analysis = forGrant.find(x => x.output_type === "grant_analysis");
        setActiveAnalysis(analysis || null);
        setDrafts(forGrant.filter(x => x.output_type !== "grant_analysis"));
      } else {
        setActiveAnalysis(null);
        setDrafts([]);
      }
    } catch (e) { console.debug("assignments/outputs: load failed", e?.message); }
  }, [staffId, activeGrantId]);

  const loadPreferences = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/${staffId}/preferences`);
      setPreferences(data);
    } catch (e) { console.debug("preferences: load failed", e?.message); }
  }, [staffId]);

  const loadMemory = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/${staffId}/memory`);
      setMemory(data);
    } catch (e) { console.debug("memory: load failed", e?.message); }
  }, [staffId]);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/${staffId}/jobs`, { params: { limit: 15 } });
      setJobs(data);
    } catch (e) { console.debug("jobs: load failed", e?.message); }
  }, [staffId]);

  useEffect(() => { loadThreads(); loadGrants(); loadIntakes(); loadPreferences(); loadMemory(); loadJobs(); }, [loadThreads, loadGrants, loadIntakes, loadPreferences, loadMemory, loadJobs]);
  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => { loadAssignmentsAndDrafts(); }, [loadAssignmentsAndDrafts]);

  useEffect(() => {
    // 2026-02-12 · Delivery repair · scroll-behavior correction.
    // Previously: scrolled to container.scrollHeight on every message-count
    // change — this pushed long substantive replies above the viewport
    // whenever a short follow-up (meta) message was added afterwards.
    // Now: if the newest message is founder's OWN send, scroll to the bottom
    // (so they see their own send + the reply arrives below); if the newest
    // message is a Grant Writer reply, scroll to the TOP of that reply so the
    // Founder reads it from Section 1 forward. If a short reply follows a
    // longer substantive one, the substantive reply is NOT pushed off-screen.
    if (!messagesEndRef.current) return;
    const container = messagesEndRef.current;
    const last = messages[messages.length - 1];
    if (!last) return;
    if (last.role === "founder") {
      container.scrollTop = container.scrollHeight;
      return;
    }
    // Staff message: scroll so its top is at the top of the visible area.
    // Find its rendered node via data-testid attributes that MessageBubble
    // sets; if the search fails, fall back to no auto-scroll (never worse
    // than pushing content off-screen).
    const wrappers = container.querySelectorAll('[data-testid^="msg-wrapper-"]');
    const lastEl = wrappers[wrappers.length - 1];
    if (lastEl) {
      const offset = lastEl.offsetTop - container.offsetTop;
      container.scrollTop = Math.max(0, offset - 8);
    }
  }, [messages]);

  // ---------- job polling ----------
  useEffect(() => {
    if (!activeJob || activeJob.status === "complete" || activeJob.status === "failed") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/staff/${staffId}/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status === "complete") {
          toast.success("Ready for review.");
          loadAssignmentsAndDrafts();
          loadJobs();
        } else if (data.status === "failed") {
          toast.error(`Failed: ${data.error || "Unknown error"}`);
          loadJobs();
        }
      } catch (e) { console.debug("job poll: transient", e?.message); }
    }, 3000);
    return () => clearInterval(t);
  }, [activeJob, staffId, loadAssignmentsAndDrafts, loadJobs]);

  // ---------- conversation ----------
  const sendMessage = async () => {
    const content = chatDraft.trim();
    if (!content || sending) return;
    setSending(true);
    // optimistic add
    setMessages(m => [...m, { id: `tmp-${Date.now()}`, role: "founder", content, created_at: new Date().toISOString() }]);
    setChatDraft("");
    try {
      await api.post(`/staff/${staffId}/conversation`, { thread_key: currentThread, content });
      await loadMessages();
      loadThreads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  // ---------- cost gate ----------
  const requestCostAndRun = async (task, inputLen, run) => {
    try {
      const { data } = await api.post(`/staff/${staffId}/cost-estimate`, {
        task, input_text_length: inputLen || 0,
      });
      setConfirmCost({ task, estimate: data, onConfirm: async () => {
        setConfirmCost(null);
        try { await run(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      }});
    } catch (e) {
      // If cost estimate fails, run anyway (don't block)
      try { await run(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
  };

  // ---------- analyze / draft actions ----------
  const startAnalyze = (grantId) => {
    const g = grants.find(x => x.id === grantId);
    requestCostAndRun("analyze_grant", (g?.notes?.length || 0), async () => {
      const { data } = await api.post(`/staff/${staffId}/analyze`, { grant_id: grantId });
      setActiveJob(data);
      toast.info("Analyzing…");
    });
  };

  const startDraft = (grantId, draftType, sourceOutputId) => {
    requestCostAndRun(draftType, 3000, async () => {
      const { data } = await api.post(`/staff/${staffId}/draft`, {
        grant_id: grantId, draft_type: draftType, source_output_id: sourceOutputId || undefined,
      });
      setActiveJob(data);
      toast.info(`Drafting ${DRAFT_TYPES.find(d => d.key === draftType)?.label || draftType}…`);
    });
  };

  // ---------- approval actions ----------
  const approve = async (outputId) => {
    try { await api.post(`/staff/outputs/${outputId}/approve`); toast.success("Approved."); loadAssignmentsAndDrafts(); loadPreferences(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (outputId, notes) => {
    try { await api.post(`/staff/outputs/${outputId}/reject`, { notes }); toast.success("Rejected. Feedback recorded."); loadAssignmentsAndDrafts(); loadPreferences(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const requestRevision = async (outputId, notes) => {
    try { const { data } = await api.post(`/staff/outputs/${outputId}/request-revision`, { notes }); toast.success("Revision requested. New draft generating."); loadAssignmentsAndDrafts(); if (data?.revision_notes) toast.info("Feedback captured.", { duration: 2500 }); loadJobs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editAndApprove = async (outputId, editedText, notes) => {
    try { await api.post(`/staff/outputs/${outputId}/edit-and-approve`, { edited_text: editedText, notes }); toast.success("Edited and approved."); loadAssignmentsAndDrafts(); loadPreferences(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---------- derived ----------
  const activeGrant = grants.find(g => g.id === activeGrantId) || null;
  const pendingOutputs = drafts.filter(d => d.approval_status === "pending");
  const grantThreadKey = activeGrantId ? `grant:${activeGrantId}` : null;

  // ---------- render ----------
  return (
    <div className="space-y-5" data-testid="staff-workspace">
      {/* Discovery Panel — the AI's opening screen per Founder directive */}
      <DiscoveryPanel onImport={async (grantId) => {
        await loadGrants();
        setActiveGrantId(grantId);
        setCurrentThread(`grant:${grantId}`);
        toast.success("Opened in Pipeline. Ready to analyze.");
      }} />

      {/* Readiness Dashboard — executive command center */}
      <ReadinessDashboard staffId={staffId} onOpenGrant={(gid) => { setActiveGrantId(gid); setCurrentThread(`grant:${gid}`); }} />

      {/* Quick Actions Bar */}
      <div className="rounded-xl border border-line bg-bone/40 p-3 flex flex-wrap gap-2 items-center justify-between" data-testid="workspace-quick-actions">
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <Sparkles className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
          <span>{staffName}&rsquo;s Office · draft-only workspace</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <QuickBtn icon={FilePlus2} label="New Intake" onClick={() => setShowIntake(true)} testId="qa-new-intake" />
          <QuickBtn icon={Wand2} label="Analyze Grant" disabled={!activeGrantId} onClick={() => startAnalyze(activeGrantId)} testId="qa-analyze" />
          <QuickBtn icon={FileText} label="Draft Proposal" disabled={!activeAnalysis || activeAnalysis.approval_status === "rejected"} onClick={() => setExpandedPanel("drafts")} testId="qa-draft" />
          <QuickBtn icon={Eye} label={`Pending Drafts${pendingOutputs.length ? ` · ${pendingOutputs.length}` : ""}`} tone={pendingOutputs.length ? "terracotta" : ""} onClick={() => setExpandedPanel("analysis")} testId="qa-pending" />
        </div>
      </div>

      {/* Grant selector + Job progress */}
      <div className="rounded-xl border border-line bg-admin-surface p-4 space-y-3" data-testid="workspace-active-selector">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="overline">Active Grant</div>
          <select
            value={activeGrantId || ""}
            onChange={e => { setActiveGrantId(e.target.value || null); setCurrentThread(e.target.value ? `grant:${e.target.value}` : "global"); }}
            data-testid="workspace-grant-select"
            className="flex-1 min-w-[240px] h-9 rounded-md border border-line bg-bone px-3 text-sm"
          >
            <option value="">— None selected —</option>
            {grants.map(g => (
              <option key={g.id} value={g.id}>{g.name}{g.funder ? ` · ${g.funder}` : ""}</option>
            ))}
          </select>
          {activeGrantId && (
            <Button variant="outline" size="sm" onClick={() => { setActiveGrantId(null); setCurrentThread("global"); }} data-testid="workspace-clear-grant" className="border-line">
              Clear
            </Button>
          )}
        </div>

        {activeJob && activeJob.status !== "complete" && activeJob.status !== "failed" && (
          <JobProgress job={activeJob} onDismiss={() => setActiveJob(null)} />
        )}
      </div>

      {/* Main workspace grid */}
      <div className={`grid gap-4 ${compact ? "" : "lg:grid-cols-12"}`}>
        {/* Left column — Conversation */}
        <section className={`${compact ? "" : "lg:col-span-4"} rounded-xl border border-line bg-admin-surface flex flex-col`} style={{ minHeight: 500 }} data-testid="workspace-conversation">
          <div className="px-4 py-3 border-b border-line bg-bone/40 flex items-center justify-between">
            <div className="overline flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Conversation</div>
            <select
              value={currentThread}
              onChange={e => setCurrentThread(e.target.value)}
              data-testid="workspace-thread-select"
              className="text-xs h-7 rounded border border-line bg-bone px-2"
            >
              <option value="global">Global thread</option>
              {grantThreadKey && <option value={grantThreadKey}>Grant: {activeGrant?.name}</option>}
              {threads.filter(t => t.thread_key !== "global" && t.thread_key !== grantThreadKey).map(t => (
                <option key={t.thread_key} value={t.thread_key}>Grant: {t.label}</option>
              ))}
            </select>
          </div>

          <div ref={messagesEndRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ maxHeight: 520 }}>
            {messages.length === 0 ? (
              <div className="text-sm text-ink-soft italic text-center py-8">
                Start a conversation. Ask for an analysis, compare grants, or request draft revisions.
              </div>
            ) : messages.map(m => (
              <MessageBubble key={m.id} m={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-ink-soft italic">
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> Senior Grant Writer is thinking…
              </div>
            )}
          </div>

          <div className="border-t border-line p-3 space-y-2">
            <Textarea
              rows={2}
              value={chatDraft}
              onChange={e => setChatDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask the Grant Writer… (⌘/Ctrl+Enter to send)"
              data-testid="workspace-chat-input"
              className="text-sm resize-none"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-ink-soft">{currentThread === "global" ? "Global institutional thread" : "This message will be indexed to this grant"}</span>
              <Button size="sm" onClick={sendMessage} disabled={sending || !chatDraft.trim()} data-testid="workspace-chat-send" className="bg-forest text-bone hover:bg-ink rounded-full">
                <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Send
              </Button>
            </div>
          </div>
        </section>

        {/* Middle column — Active canvas */}
        <section className={`${compact ? "" : "lg:col-span-5"} space-y-3`} data-testid="workspace-canvas">
          {!activeGrantId ? (
            <>
              <RecommendationsPanel
                staffId={staffId}
                onBeginDrafting={(grantId) => { loadGrants(); setActiveGrantId(grantId); }}
              />
              <div className="rounded-xl border border-dashed border-line bg-bone/30 p-6 text-center" data-testid="canvas-empty">
                <FolderOpen className="h-6 w-6 text-ink-soft mx-auto mb-2" strokeWidth={1.5} />
                <div className="font-serif text-sm text-ink">Or bring in a specific grant</div>
                <p className="text-xs text-ink-soft mt-1 max-w-md mx-auto leading-relaxed">
                  Use <strong>New Intake</strong> for a grant not surfaced by Discovery.
                </p>
                <Button onClick={() => setShowIntake(true)} data-testid="canvas-new-intake" className="mt-3 bg-forest text-bone hover:bg-ink rounded-full">
                  <FilePlus2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Bring in a grant
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Analysis Panel */}
              <AnalysisPanel
                grant={activeGrant}
                analysis={activeAnalysis}
                onAnalyze={() => startAnalyze(activeGrantId)}
                onApprove={approve}
                onReject={reject}
                onRequestRevision={requestRevision}
                onEditAndApprove={editAndApprove}
                expanded={expandedPanel === "analysis"}
                onToggle={() => setExpandedPanel(expandedPanel === "analysis" ? null : "analysis")}
              />

              {/* Drafts Panel */}
              <DraftsPanel
                grant={activeGrant}
                analysis={activeAnalysis}
                drafts={drafts}
                onStartDraft={(dt) => startDraft(activeGrantId, dt, activeAnalysis?.id)}
                onApprove={approve}
                onReject={reject}
                onRequestRevision={requestRevision}
                onEditAndApprove={editAndApprove}
                expanded={expandedPanel === "drafts"}
                onToggle={() => setExpandedPanel(expandedPanel === "drafts" ? null : "drafts")}
              />
            </>
          )}
        </section>

        {/* Right column — Memory + Preferences + History */}
        <aside className={`${compact ? "" : "lg:col-span-3"} space-y-3`} data-testid="workspace-sidebar">
          <MemoryPanel memory={memory} onOpenGrant={(gid) => { setActiveGrantId(gid); setCurrentThread(`grant:${gid}`); }} activeGrantId={activeGrantId} />
          <PreferencesPanel preferences={preferences} />
          <AssignmentTimeline assignments={assignments} jobs={jobs} onOpen={(gid) => { setActiveGrantId(gid); setCurrentThread(`grant:${gid}`); }} />
        </aside>
      </div>

      {/* Intake modal */}
      {showIntake && (
        <IntakeModal
          staffId={staffId}
          onClose={() => setShowIntake(false)}
          onCreated={async (grantId) => {
            setShowIntake(false);
            await loadGrants();
            await loadIntakes();
            await loadJobs();
            setActiveGrantId(grantId);
            setCurrentThread(`grant:${grantId}`);
            // The convert endpoint already kicked off a job — pick it up
            const { data } = await api.get(`/staff/${staffId}/jobs`);
            if (data && data.length) setActiveJob(data[0]);
          }}
          confirmCost={confirmCost ? null : (task, len, run) => requestCostAndRun(task, len, run)}
        />
      )}

      {/* Cost confirmation modal */}
      {confirmCost && (
        <CostConfirmModal
          confirmCost={confirmCost}
          onCancel={() => setConfirmCost(null)}
        />
      )}
    </div>
  );
}

// =========================================================================
// Sub-components
// =========================================================================

function QuickBtn({ icon: Icon, label, onClick, disabled, tone, testId }) {
  const toneCls = tone === "terracotta"
    ? "bg-terracotta text-bone hover:bg-terracotta/90"
    : "bg-bone text-ink hover:bg-line border border-line";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors duration-150 ${toneCls} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      {label}
    </button>
  );
}

function MessageBubble({ m }) {
  const isFounder = m.role === "founder";
  const isSystem = m.role === "system";
  const content = m.content || "";
  // 2026-02-12 · Delivery repair: substantive Grant Writer output (>= 800 chars
  // or containing markdown headings/lists/tables) needs more horizontal space
  // and Markdown rendering so it's readable rather than a wall of raw hashes.
  const isSubstantive = !isFounder && !isSystem && (
    content.length >= 800 ||
    /^#{1,6}\s|^[-*+]\s|^\d+\.\s|\n\|.+\|/m.test(content)
  );
  const widthCls = isFounder
    ? "max-w-[85%]"
    : isSubstantive
      ? "max-w-[95%] w-full"
      : "max-w-[85%]";
  return (
    <div className={`flex ${isFounder ? "justify-end" : "justify-start"}`} data-testid={`msg-wrapper-${m.role}`}>
      <div className={`${widthCls} rounded-lg px-3 py-2 text-sm leading-relaxed ${
        isFounder ? "bg-forest text-bone whitespace-pre-wrap" :
        isSystem ? "bg-bone/60 text-ink-soft italic border border-line whitespace-pre-wrap" :
        "bg-bone border border-line text-ink"
      }`} data-testid={`msg-${m.role}`}>
        {!isFounder && !isSystem && (
          <div className="flex items-center justify-between mb-1">
            <div className="text-[9px] uppercase tracking-widest text-terracotta">Senior Grant Writer</div>
            {isSubstantive && (
              <span className="text-[9px] uppercase tracking-widest text-ink-soft" data-testid="msg-substantive-flag">
                Substantive · {content.length.toLocaleString()} chars
              </span>
            )}
          </div>
        )}
        {isFounder || isSystem ? (
          content
        ) : (
          <MarkdownContent text={content} />
        )}
      </div>
    </div>
  );
}

function JobProgress({ job, onDismiss }) {
  const ms = job.milestones || [];
  return (
    <div className="rounded-lg border border-terracotta/40 bg-terracotta/5 p-3" data-testid="job-progress">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-terracotta flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
          Working · {job.progress || 0}%
        </div>
        {job.status === "failed" && (
          <button onClick={onDismiss} className="text-xs text-ink-soft hover:text-terracotta">Dismiss</button>
        )}
      </div>
      <div className="h-1.5 bg-bone rounded-full overflow-hidden mb-3">
        <div className="h-full bg-terracotta transition-all duration-500" style={{ width: `${job.progress || 0}%` }} />
      </div>
      <ul className="space-y-1.5 text-xs">
        {ms.map(m => (
          <li key={m.key} className="flex items-center gap-2">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${
              m.status === "done" ? "bg-forest" :
              m.status === "running" ? "bg-terracotta animate-pulse" :
              "bg-line"
            }`} />
            <span className={m.status === "done" ? "text-ink" : m.status === "running" ? "text-terracotta font-medium" : "text-ink-soft"}>{m.label}</span>
            {m.detail && <span className="text-[10px] text-ink-soft italic">— {m.detail}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Analysis Panel ----------
function AnalysisPanel({ grant, analysis, onAnalyze, onApprove, onReject, onRequestRevision, onEditAndApprove, expanded, onToggle }) {
  const [rejectNote, setRejectNote] = useState("");
  const [revisionNote, setRevisionNote] = useState("");
  const [showRevision, setShowRevision] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [editNote, setEditNote] = useState("");

  const p = analysis?.payload || {};

  const startEdit = () => {
    setEditedText(p.draft_narrative || "");
    setEditing(true);
  };

  return (
    <div className="rounded-xl border border-line bg-admin-surface" data-testid="analysis-panel">
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between border-b border-line bg-bone/40">
        <div className="overline flex items-center gap-2">
          <Wand2 className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
          Structured Analysis
          {analysis && (
            <Badge className={`ml-2 text-[10px] uppercase tracking-widest ${
              analysis.approval_status === "approved" ? "bg-forest text-bone" :
              analysis.approval_status === "rejected" ? "bg-line text-ink-soft" :
              "bg-terracotta text-bone"
            }`}>{analysis.approval_status}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {analysis && (
            <div className="text-xs text-ink-soft">
              Priority <strong className="text-ink">{p.priority_score ?? "—"}</strong>{" · "}
              Fit <strong className="text-ink">{p.fit_score ?? "—"}</strong>
            </div>
          )}
          <ChevronRight className={`h-4 w-4 text-ink-soft transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} strokeWidth={1.5} />
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {!analysis ? (
            <div className="text-center py-6" data-testid="analysis-empty">
              <p className="text-sm text-ink-soft mb-3">No analysis yet for <strong>{grant?.name}</strong>.</p>
              <Button onClick={onAnalyze} data-testid="analysis-start" className="bg-forest text-bone hover:bg-ink rounded-full">
                <Wand2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Run structured analysis
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md bg-terracotta/10 border border-terracotta/30 px-3 py-2 text-xs text-terracotta flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                AI-generated draft. Requires founder review. Nothing is submitted externally.
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <ReportField label="Grant Summary">{p.grant_summary}</ReportField>
                <ReportField label="Mission Fit">{p.mission_fit}</ReportField>
                <ReportField label="Funding Amount">{p.funding_amount || "—"}</ReportField>
                <ReportField label="Deadline">{p.deadline || "—"}</ReportField>
              </div>

              <ReportListField label="Eligibility" items={p.eligibility} />
              <ReportListField label="Required Documents" items={p.required_documents} />
              <ReportListField label="Matching NEOS Programs" items={p.matching_programs} />
              <ReportListField label="Strengths" items={p.strengths} />
              <ReportListField label="Weaknesses" items={p.weaknesses} />
              <ReportListField label="Risks" items={p.risks} tone="terracotta" />
              <ReportListField label="Questions to Confirm" items={p.questions} />

              {/* Sprint · Grant Writer Stabilization additions */}
              {(p.verified_assets?.length > 0 || p.missing_documents?.length > 0 ||
                p.founder_confirmation_required?.length > 0 || p.critical_risks?.length > 0 ||
                p.recommended_next_steps?.length > 0) && (
                <div className="border-t border-line pt-4 mt-2 space-y-4" data-testid="doctrine-fields">
                  <div className="text-[10px] uppercase tracking-widest text-terracotta">Doctrine-Governed Sections</div>
                  <ReportListField label="Verified Assets (Evidence Level 1–2)" items={p.verified_assets} />
                  <ReportListField label="Missing Documents" items={p.missing_documents} tone="terracotta" />
                  <ReportListField label="Founder Confirmation Required (Rule 3)" items={p.founder_confirmation_required} tone="terracotta" />
                  <ReportListField label="Critical Risks (Rules 1, 6, 9)" items={p.critical_risks} tone="terracotta" />
                  <ReportListField label="Recommended Next Steps" items={p.recommended_next_steps} />
                </div>
              )}

              <ReportField label="Recommended Next Step" italic>{p.recommended_next_step}</ReportField>

              {p.draft_narrative && !editing && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-ink-soft">Draft Narrative</div>
                    {analysis.approval_status === "pending" && (
                      <button onClick={startEdit} className="text-xs text-terracotta hover:underline flex items-center gap-1" data-testid="analysis-edit">
                        <Edit3 className="h-3 w-3" strokeWidth={1.5} /> Edit
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-ink leading-relaxed whitespace-pre-wrap bg-bone/50 border border-line rounded p-4 max-h-72 overflow-y-auto" data-testid="analysis-narrative">
                    {p.draft_narrative}
                  </div>
                </div>
              )}
              {editing && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1.5">Edit Draft Narrative</div>
                  <Textarea rows={10} value={editedText} onChange={e => setEditedText(e.target.value)} data-testid="analysis-edit-textarea" />
                  <Input placeholder="Optional: note what you changed and why" value={editNote} onChange={e => setEditNote(e.target.value)} className="mt-2" data-testid="analysis-edit-note" />
                  <div className="flex gap-2 mt-2">
                    <Button onClick={() => { onEditAndApprove(analysis.id, editedText, editNote); setEditing(false); }} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid="analysis-edit-approve">
                      <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Save edit &amp; approve
                    </Button>
                    <Button variant="outline" onClick={() => setEditing(false)} className="border-line">Cancel</Button>
                  </div>
                </div>
              )}

              {p.error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">Parse error: {p.error}</div>
              )}

              {/* Approval controls */}
              {analysis.approval_status === "pending" && !editing && (
                <ApprovalBar
                  onApprove={() => onApprove(analysis.id)}
                  onReject={(notes) => onReject(analysis.id, notes)}
                  onRequestRevision={(notes) => onRequestRevision(analysis.id, notes)}
                  rejectNote={rejectNote} setRejectNote={setRejectNote}
                  revisionNote={revisionNote} setRevisionNote={setRevisionNote}
                  showRevision={showRevision} setShowRevision={setShowRevision}
                  outputId={analysis.id}
                />
              )}

              {analysis.approval_notes && (
                <div className="text-xs text-ink-soft italic border-t border-line pt-2">
                  <strong>Feedback recorded:</strong> {analysis.approval_notes}
                </div>
              )}
              {analysis.revision_notes && (
                <div className="text-xs text-terracotta italic border-t border-line pt-2">
                  <strong>Revision requested:</strong> {analysis.revision_notes}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ReportField({ label, children, italic }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">{label}</div>
      <p className={`text-sm text-ink leading-relaxed ${italic ? "italic" : ""}`}>{children || "—"}</p>
    </div>
  );
}

function ReportListField({ label, items, tone }) {
  if (!items || items.length === 0) return null;
  const bullet = tone === "terracotta" ? "text-terracotta" : "text-ink-soft";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1.5">{label}</div>
      <ul className="text-sm text-ink space-y-1">
        {items.map((it, i) => (
          <li key={`${label}-${i}-${(it || "").slice(0, 24)}`} className="flex items-start gap-2"><span className={`${bullet} mt-1`}>·</span>{it}</li>
        ))}
      </ul>
    </div>
  );
}

function ApprovalBar({ onApprove, onReject, onRequestRevision, rejectNote, setRejectNote, revisionNote, setRevisionNote, showRevision, setShowRevision, outputId }) {
  return (
    <div className="border-t border-line pt-4 space-y-3">
      <div className="text-xs text-ink-soft leading-relaxed">
        <strong>Your review</strong> — Approve records this draft as founder-cleared. Reject captures feedback for the record. Request Revision spawns a new draft with your notes as guidance.
      </div>
      {!showRevision ? (
        <>
          <Textarea rows={2} placeholder="Feedback if rejecting (optional but useful)…" value={rejectNote} onChange={e => setRejectNote(e.target.value)} data-testid={`reject-note-${outputId}`} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={onApprove} data-testid={`approve-${outputId}`} className="bg-forest text-bone hover:bg-ink rounded-full">
              <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Approve
            </Button>
            <Button onClick={() => onReject(rejectNote)} variant="outline" className="border-line" data-testid={`reject-${outputId}`}>
              <XCircle className="h-4 w-4 mr-2" strokeWidth={1.5} /> Reject
            </Button>
            <Button onClick={() => setShowRevision(true)} variant="outline" className="border-line text-terracotta hover:bg-terracotta/10" data-testid={`request-revision-${outputId}`}>
              <RotateCw className="h-4 w-4 mr-2" strokeWidth={1.5} /> Request revision
            </Button>
          </div>
        </>
      ) : (
        <>
          <Textarea rows={3} placeholder="What should change in the revision? Be specific — this becomes the guidance for the new draft." value={revisionNote} onChange={e => setRevisionNote(e.target.value)} data-testid={`revision-note-${outputId}`} />
          <div className="flex gap-2">
            <Button onClick={() => { onRequestRevision(revisionNote); setShowRevision(false); setRevisionNote(""); }} disabled={!revisionNote.trim()} className="bg-terracotta text-bone hover:bg-terracotta/90 rounded-full" data-testid={`submit-revision-${outputId}`}>
              <RotateCw className="h-4 w-4 mr-2" strokeWidth={1.5} /> Send revision request
            </Button>
            <Button variant="outline" onClick={() => setShowRevision(false)} className="border-line">Cancel</Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Drafts Panel ----------
function DraftsPanel({ grant, analysis, drafts, onStartDraft, onApprove, onReject, onRequestRevision, onEditAndApprove, expanded, onToggle }) {
  const [openDraft, setOpenDraft] = useState(null);

  return (
    <div className="rounded-xl border border-line bg-admin-surface" data-testid="drafts-panel">
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between border-b border-line bg-bone/40">
        <div className="overline flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
          Drafts · {drafts.length}
        </div>
        <ChevronRight className={`h-4 w-4 text-ink-soft transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} strokeWidth={1.5} />
      </button>

      {expanded && (
        <div className="p-4 space-y-3">
          {/* Draft type launcher */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Generate a new draft</div>
            <div className="flex flex-wrap gap-2">
              {DRAFT_TYPES.map(dt => (
                <button
                  key={dt.key}
                  onClick={() => onStartDraft(dt.key)}
                  disabled={!analysis}
                  data-testid={`draft-start-${dt.key}`}
                  className="h-8 px-3 rounded-full text-xs bg-bone text-ink hover:bg-line border border-line disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FilePlus2 className="h-3 w-3 mr-1.5 inline" strokeWidth={1.5} /> {dt.short}
                </button>
              ))}
            </div>
            {!analysis && (
              <div className="text-[11px] text-ink-soft italic mt-2">Run the analysis first — drafts inherit the analysis context.</div>
            )}
          </div>

          {drafts.length === 0 ? (
            <div className="text-sm text-ink-soft italic text-center py-6">No drafts yet for this grant.</div>
          ) : (
            <ul className="space-y-2">
              {drafts.map(d => (
                <DraftItem
                  key={d.id}
                  draft={d}
                  open={openDraft === d.id}
                  onToggle={() => setOpenDraft(openDraft === d.id ? null : d.id)}
                  onApprove={onApprove}
                  onReject={onReject}
                  onRequestRevision={onRequestRevision}
                  onEditAndApprove={onEditAndApprove}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function DraftItem({ draft, open, onToggle, onApprove, onReject, onRequestRevision, onEditAndApprove }) {
  const [rejectNote, setRejectNote] = useState("");
  const [revisionNote, setRevisionNote] = useState("");
  const [showRevision, setShowRevision] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [editNote, setEditNote] = useState("");

  const label = DRAFT_TYPES.find(x => x.key === draft.output_type)?.label || draft.output_type;
  const p = draft.payload || {};
  const text = p.approved_text || p.draft_text || "";
  const tu = draft.token_usage || {};

  const startEdit = () => { setEditedText(text); setEditing(true); };

  return (
    <li className="border border-line rounded-lg overflow-hidden" data-testid={`draft-${draft.id}`}>
      <button onClick={onToggle} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-bone/40 text-left">
        <FileText className="h-4 w-4 text-terracotta flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink truncate">{label}</div>
          <div className="text-[10px] text-ink-soft uppercase tracking-wider mt-0.5">
            {new Date(draft.created_at).toLocaleString()} · ~{tu.estimated_total || 0} tok
          </div>
        </div>
        <Badge className={`text-[10px] uppercase tracking-widest ${
          draft.approval_status === "approved" ? "bg-forest text-bone" :
          draft.approval_status === "rejected" ? "bg-line text-ink-soft" :
          "bg-terracotta text-bone"
        }`}>{draft.approval_status}</Badge>
        <ChevronRight className={`h-4 w-4 text-ink-soft transition-transform ${open ? "rotate-90" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-line bg-bone/20 space-y-3">
          {!editing ? (
            <>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">Draft text</div>
                {draft.approval_status === "pending" && (
                  <button onClick={startEdit} className="text-xs text-terracotta hover:underline flex items-center gap-1" data-testid={`draft-edit-${draft.id}`}>
                    <Edit3 className="h-3 w-3" strokeWidth={1.5} /> Edit
                  </button>
                )}
              </div>
              <div className="text-sm text-ink leading-relaxed whitespace-pre-wrap bg-admin-surface border border-line rounded p-3 max-h-80 overflow-y-auto" data-testid={`draft-text-${draft.id}`}>{text}</div>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">Edit</div>
              <Textarea rows={12} value={editedText} onChange={e => setEditedText(e.target.value)} data-testid={`draft-edit-textarea-${draft.id}`} />
              <Input placeholder="Optional: note what you changed and why" value={editNote} onChange={e => setEditNote(e.target.value)} data-testid={`draft-edit-note-${draft.id}`} />
              <div className="flex gap-2">
                <Button onClick={() => { onEditAndApprove(draft.id, editedText, editNote); setEditing(false); }} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid={`draft-edit-approve-${draft.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Save edit &amp; approve
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="border-line">Cancel</Button>
              </div>
            </>
          )}

          {draft.approval_status === "pending" && !editing && (
            <ApprovalBar
              onApprove={() => onApprove(draft.id)}
              onReject={(n) => onReject(draft.id, n)}
              onRequestRevision={(n) => onRequestRevision(draft.id, n)}
              rejectNote={rejectNote} setRejectNote={setRejectNote}
              revisionNote={revisionNote} setRevisionNote={setRevisionNote}
              showRevision={showRevision} setShowRevision={setShowRevision}
              outputId={draft.id}
            />
          )}
          {draft.approval_notes && draft.approval_status !== "pending" && (
            <div className="text-xs text-ink-soft italic border-t border-line pt-2"><strong>Feedback:</strong> {draft.approval_notes}</div>
          )}
        </div>
      )}
    </li>
  );
}

// ---------- Memory Panel ----------
function MemoryPanel({ memory, onOpenGrant, activeGrantId }) {
  const grants = memory?.past_grants || [];
  return (
    <div className="rounded-xl border border-line bg-admin-surface" data-testid="memory-panel">
      <div className="px-4 py-3 border-b border-line bg-bone/40">
        <div className="overline flex items-center gap-2"><BookMarked className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Institutional Memory</div>
      </div>
      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
        {grants.length === 0 ? (
          <div className="text-xs text-ink-soft italic py-3 text-center">No grants worked yet.</div>
        ) : grants.map(g => (
          <button
            key={g.id}
            onClick={() => onOpenGrant(g.id)}
            data-testid={`memory-grant-${g.id}`}
            className={`w-full text-left px-3 py-2 rounded border transition-colors ${
              activeGrantId === g.id ? "border-terracotta bg-terracotta/5" : "border-line bg-bone/40 hover:bg-bone"
            }`}
          >
            <div className="text-xs font-medium text-ink truncate">{g.name}</div>
            <div className="text-[10px] text-ink-soft mt-0.5 flex items-center gap-2">
              <span>{g.funder || "—"}</span>
              {g.outputs?.approved > 0 && <span className="text-forest">✓{g.outputs.approved}</span>}
              {g.outputs?.pending > 0 && <span className="text-terracotta">⏳{g.outputs.pending}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Preferences Panel ----------
function PreferencesPanel({ preferences }) {
  const [tab, setTab] = useState("rejections");
  if (!preferences) return null;
  const rej = preferences.rejections_recent || [];
  const rev = preferences.revision_requests_recent || [];
  const edited = preferences.edited_approvals_recent || [];
  const reused = preferences.reused_language || [];

  return (
    <div className="rounded-xl border border-line bg-admin-surface" data-testid="preferences-panel">
      <div className="px-4 py-3 border-b border-line bg-bone/40">
        <div className="overline flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Founder Preferences</div>
        <div className="text-[10px] text-ink-soft italic mt-1">Captured only — not yet injected into future drafts.</div>
      </div>
      <div className="border-b border-line px-3 flex gap-3 text-[10px] uppercase tracking-widest">
        {[
          ["rejections", `Rej · ${rej.length}`],
          ["revisions", `Rev · ${rev.length}`],
          ["edits", `Edits · ${edited.length}`],
          ["language", `Voice · ${reused.length}`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`py-2 border-b-2 transition-colors ${tab === k ? "border-terracotta text-terracotta" : "border-transparent text-ink-soft hover:text-ink"}`} data-testid={`pref-tab-${k}`}>{l}</button>
        ))}
      </div>
      <div className="p-3 max-h-56 overflow-y-auto text-xs">
        {tab === "rejections" && (
          rej.length === 0 ? <div className="text-ink-soft italic">No rejections yet.</div> :
          <ul className="space-y-2">
            {rej.slice(0, 10).map((r) => (
              <li key={`rej-${r.approved_at || r.target_name}`} className="border-l-2 border-terracotta pl-2">
                <div className="text-ink font-medium truncate">{r.target_name || r.output_type}</div>
                <div className="text-ink-soft italic mt-0.5">{r.approval_notes}</div>
              </li>
            ))}
          </ul>
        )}
        {tab === "revisions" && (
          rev.length === 0 ? <div className="text-ink-soft italic">No revision requests yet.</div> :
          <ul className="space-y-2">
            {rev.slice(0, 10).map((r) => (
              <li key={`rev-${r.revision_requested_at || r.target_name}`} className="border-l-2 border-terracotta pl-2">
                <div className="text-ink font-medium truncate">{r.target_name}</div>
                <div className="text-ink-soft italic mt-0.5">{r.revision_notes}</div>
              </li>
            ))}
          </ul>
        )}
        {tab === "edits" && (
          edited.length === 0 ? <div className="text-ink-soft italic">No founder-edited approvals yet.</div> :
          <ul className="space-y-2">
            {edited.slice(0, 10).map((r) => (
              <li key={`edit-${r.approved_at || r.target_name}`} className="border-l-2 border-forest pl-2">
                <div className="text-ink font-medium truncate">{r.target_name}</div>
                {r.payload?.founder_notes && <div className="text-ink-soft italic mt-0.5">{r.payload.founder_notes}</div>}
              </li>
            ))}
          </ul>
        )}
        {tab === "language" && (
          reused.length === 0 ? <div className="text-ink-soft italic">Reused language will surface after several approved drafts.</div> :
          <ul className="space-y-2">
            {reused.slice(0, 10).map((r) => (
              <li key={`lang-${(r.sentence || "").slice(0, 48)}`} className="border-l-2 border-forest pl-2">
                <div className="text-ink">{r.sentence}</div>
                <div className="text-ink-soft mt-0.5">Used {r.count} times</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Assignment Timeline ----------
function AssignmentTimeline({ assignments, jobs, onOpen }) {
  const items = useMemo(() => {
    const jobById = {};
    jobs.forEach(j => { jobById[j.target_id] = j; });
    return (assignments || []).slice(0, 10);
  }, [assignments, jobs]);

  return (
    <div className="rounded-xl border border-line bg-admin-surface" data-testid="timeline-panel">
      <div className="px-4 py-3 border-b border-line bg-bone/40">
        <div className="overline flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Assignment History</div>
      </div>
      <div className="p-3 max-h-52 overflow-y-auto space-y-2">
        {items.length === 0 ? (
          <div className="text-xs text-ink-soft italic py-2 text-center">No assignments yet.</div>
        ) : items.map(a => (
          <button
            key={a.id}
            onClick={() => a.target_id && onOpen(a.target_id)}
            data-testid={`timeline-item-${a.id}`}
            className="w-full text-left px-3 py-2 rounded border border-line bg-bone/40 hover:bg-bone"
          >
            <div className="text-xs font-medium text-ink truncate">{a.target_name || a.target_id}</div>
            <div className="text-[10px] text-ink-soft mt-0.5 flex items-center gap-2">
              <Badge className={`text-[9px] px-1.5 ${
                a.status === "complete" ? "bg-forest text-bone" :
                a.status === "failed" ? "bg-line text-red-800" :
                "bg-bone text-ink border border-line"
              }`}>{a.status}</Badge>
              <span>{new Date(a.created_at).toLocaleDateString()}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Intake Modal ----------
function IntakeModal({ staffId, onClose, onCreated }) {
  const [tab, setTab] = useState("url");
  const [url, setUrl] = useState("");
  const [paste, setPaste] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [intake, setIntake] = useState(null); // once ingested
  const [funder, setFunder] = useState("");
  const [amount, setAmount] = useState("");
  const [deadline, setDeadline] = useState("");

  const ingest = async () => {
    setBusy(true);
    try {
      let data;
      if (tab === "url") {
        if (!url.trim()) throw { response: { data: { detail: "URL is required" } } };
        ({ data } = await api.post(`/staff/${staffId}/intake/url`, { url: url.trim(), title }));
      } else if (tab === "paste") {
        if (!paste.trim()) throw { response: { data: { detail: "Please paste text" } } };
        ({ data } = await api.post(`/staff/${staffId}/intake/paste`, { text: paste, title }));
      } else if (tab === "file") {
        if (!file) throw { response: { data: { detail: "Choose a file" } } };
        const fd = new FormData();
        fd.append("file", file);
        if (title) fd.append("title", title);
        ({ data } = await api.post(`/staff/${staffId}/intake/file`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
      }
      setIntake(data);
      setTitle(data.title || "");
      toast.success("Ingested. Confirm details, then convert to grant.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const convert = async () => {
    if (!intake) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${staffId}/intakes/${intake.id}/convert-and-analyze`, {
        name: title || intake.title,
        funder: funder || "",
        amount: parseFloat(amount) || 0,
        deadline: deadline || "",
      });
      toast.success("Grant created. Analyzing…");
      onCreated(data.grant.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-admin-surface border border-line rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="intake-modal">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div>
            <div className="overline">New Intake</div>
            <h3 className="font-serif text-2xl text-ink mt-1">Bring a grant into the workspace</h3>
          </div>
          <button onClick={onClose} className="text-ink-soft hover:text-terracotta" data-testid="intake-close"><Trash2 className="h-5 w-5" strokeWidth={1.5} /></button>
        </div>

        {!intake ? (
          <div className="p-6 space-y-4">
            <div className="flex gap-2 border-b border-line">
              <IntakeTab active={tab === "url"} onClick={() => setTab("url")} icon={LinkIcon} label="Paste URL" testId="intake-tab-url" />
              <IntakeTab active={tab === "file"} onClick={() => setTab("file")} icon={Upload} label="Upload (PDF / Word / TXT)" testId="intake-tab-file" />
              <IntakeTab active={tab === "paste"} onClick={() => setTab("paste")} icon={ClipboardPaste} label="Paste Text" testId="intake-tab-paste" />
            </div>

            {tab === "url" && (
              <div className="space-y-3">
                <label className="text-xs text-ink-soft">Grant URL</label>
                <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" data-testid="intake-url" />
              </div>
            )}
            {tab === "file" && (
              <div className="space-y-3">
                <label className="text-xs text-ink-soft">Choose PDF, Word (.docx), or TXT/RFP file</label>
                <input type="file" accept=".pdf,.docx,.doc,.txt,.rfp,.md" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="intake-file" className="text-sm" />
                {file && <div className="text-xs text-ink-soft">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
              </div>
            )}
            {tab === "paste" && (
              <div className="space-y-3">
                <label className="text-xs text-ink-soft">Paste grant description or RFP text</label>
                <Textarea rows={8} value={paste} onChange={e => setPaste(e.target.value)} placeholder="Paste the full text of the grant announcement…" data-testid="intake-paste" />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs text-ink-soft">Working title (optional — will be inferred)</label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. National Endowment Round 3" data-testid="intake-title" />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
              <Button variant="outline" onClick={onClose} className="border-line">Cancel</Button>
              <Button onClick={ingest} disabled={busy} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid="intake-ingest">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} /> Extracting…</> : <><ArrowRight className="h-4 w-4 mr-2" strokeWidth={1.5} /> Extract text</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="rounded-md bg-forest/10 border border-forest/30 px-3 py-2 text-xs text-forest flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              Text extracted. Confirm the details below, then Convert &amp; Analyze.
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Extracted preview (first 800 chars)</div>
              <div className="text-xs text-ink leading-relaxed bg-bone/50 border border-line rounded p-3 max-h-40 overflow-y-auto whitespace-pre-wrap">{(intake.raw_text || "").slice(0, 800)}{(intake.raw_text || "").length > 800 ? "…" : ""}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-ink-soft">Grant name</label>
                <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="intake-final-name" />
              </div>
              <div>
                <label className="text-xs text-ink-soft">Funder</label>
                <Input value={funder} onChange={e => setFunder(e.target.value)} data-testid="intake-final-funder" />
              </div>
              <div>
                <label className="text-xs text-ink-soft">Amount (USD)</label>
                <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="intake-final-amount" />
              </div>
              <div>
                <label className="text-xs text-ink-soft">Deadline</label>
                <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} data-testid="intake-final-deadline" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
              <Button variant="outline" onClick={() => setIntake(null)} className="border-line">Back</Button>
              <Button onClick={convert} disabled={busy} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid="intake-convert">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} /> Creating…</> : <><Wand2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Convert &amp; Analyze</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IntakeTab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`px-3 py-2 text-xs flex items-center gap-2 border-b-2 transition-colors ${active ? "border-terracotta text-terracotta" : "border-transparent text-ink-soft hover:text-ink"}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {label}
    </button>
  );
}

// ---------- Cost Confirm Modal ----------
function CostConfirmModal({ confirmCost, onCancel }) {
  const e = confirmCost.estimate;
  return (
    <div className="fixed inset-0 z-[90] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-admin-surface border border-line rounded-2xl max-w-md w-full" onClick={ev => ev.stopPropagation()} data-testid="cost-modal">
        <div className="px-5 py-4 border-b border-line">
          <div className="overline flex items-center gap-2"><DollarSign className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Estimated Cost</div>
          <h3 className="font-serif text-xl text-ink mt-1">Continue with AI execution?</h3>
        </div>
        <div className="p-5 space-y-3">
          <CostRow label="Task" value={confirmCost.task.replace(/_/g, " ")} />
          <CostRow label="Estimated tokens" value={e.estimated_total_tokens.toLocaleString()} sub={`${e.estimated_input_tokens.toLocaleString()} in · ${e.estimated_output_tokens.toLocaleString()} out`} />
          <CostRow label="Estimated USD" value={`$${e.estimated_usd.toFixed(4)}`} sub={`@ $${e.pricing.input_per_million_usd}/M in, $${e.pricing.output_per_million_usd}/M out`} />
          <CostRow label="Estimated time" value={`~${e.estimated_seconds}s`} sub="Approximate — LLM speed varies" />
          <CostRow label="Model" value={e.model.split("/").pop()} />
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} className="border-line" data-testid="cost-cancel">Cancel</Button>
          <Button onClick={confirmCost.onConfirm} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid="cost-confirm">
            <Cpu className="h-4 w-4 mr-2" strokeWidth={1.5} /> Run
          </Button>
        </div>
      </div>
    </div>
  );
}

function CostRow({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <div className="text-xs text-ink-soft">{label}</div>
        {sub && <div className="text-[10px] text-ink-soft/70 italic">{sub}</div>}
      </div>
      <div className="font-serif text-lg text-ink">{value}</div>
    </div>
  );
}
